import { useMemo, useState } from 'react'
import { getEmpresas } from '../../../../services/localstorage/localstorage.service'
import { getPeriodoMesAtual } from '../../../../services/util/date.service'
import { errorHandler } from '../../../../services/util/error.service'
import ProdutividadeSeparacaoHttp from '../../../../services/api/https/dashboard/produtividade-separacao.http'
import { PickingStatus } from '../../../../constants/picking-status'

export const LOAD_MESSAGE = {
  NONE: 'NONE',
  FETCH_INITIAL_INFO: 'Adquirindo empresas',
  GET_PICKINGS: 'Adquirindo dados de produtividade',
}

const INITIAL_STATE = {
  companies: [],
  companySelected: null,
  pInicial: getPeriodoMesAtual().dataInicial,
  pFinal: getPeriodoMesAtual().dataFinal,
  loadingMessage: LOAD_MESSAGE.NONE,
  errorMessage: null,
  successMessage: null,

  // dados retornados da API
  pickings: [],
  pickingOptions: [],
  selectedPickings: [],

  // dependentes
  teamOptions: [],
  selectedTeams: [],
  userOptions: [],
  selectedUsers: [],

  report: null,
}

const PICKING_STATUS_CONFIG = [
  PickingStatus.ProntoParaDownload,
  PickingStatus.PickingConcluido,
  PickingStatus.ResumoEmitido,
]

// helpers de tempo
function timeStrToSeconds(str) {
  if (!str || str === '-' || str === '00:00:00') return 0
  const parts = str.split(':')
  if (parts.length !== 3) return 0
  const [h, m, s] = parts.map(n => parseInt(n, 10) || 0)
  return h * 3600 + m * 60 + s
}

function secondsToTimeStr(totalSeconds) {
  const sec = Math.max(0, Math.floor(totalSeconds))
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = sec % 60
  const pad = n => n.toString().padStart(2, '0')
  return `${pad(h)}:${pad(m)}:${pad(s)}`
}

function formatDateTimeToBR(dateTimeStr) {
  if (!dateTimeStr || dateTimeStr === '-') return '-'
  const parts = dateTimeStr.split(' ')
  if (parts.length !== 2) return dateTimeStr

  const [datePart, timePart] = parts
  const dateParts = datePart.split('-')
  if (dateParts.length !== 3) return dateTimeStr

  const [year, month, day] = dateParts
  return `${day}/${month}/${year} ${timePart}`
}

export default function useProdutividadeSeparacao() {
  const [state, setState] = useState(INITIAL_STATE)

  const localDispatch = useMemo(() => {
    return {
      resetState() {
        setState(prev => ({
          ...prev,
          errorMessage: null,
          successMessage: null,
          loadingMessage: LOAD_MESSAGE.NONE,
        }))
      },

      onInit() {
        setState(prev => ({ ...prev, loadingMessage: LOAD_MESSAGE.FETCH_INITIAL_INFO }))
      },

      getCompaniesAssociatedToUser() {
        try {
          const empresas = getEmpresas()
          setState(prev => ({
            ...prev,
            companies: empresas,
            companySelected: empresas && empresas.length === 1 ? empresas[0] : prev.companySelected,
            loadingMessage: LOAD_MESSAGE.NONE,
          }))
        } catch (e) {
          setState(prev => ({
            ...prev,
            errorMessage: errorHandler(e),
            loadingMessage: LOAD_MESSAGE.NONE,
          }))
        }
      },

      onCompanyChange(empresa) {
        setState(prev => ({
          ...prev,
          companySelected: empresa,
        }))
      },

      onPeriodoInicialChange(pInicial) {
        if (!pInicial) return

        let dateValue
        if (pInicial instanceof Date) {
          dateValue = pInicial
        } else if (typeof pInicial === 'string') {
          dateValue = new Date(pInicial)
        } else {
          return
        }

        if (isNaN(dateValue.getTime())) return

        setState(prev => ({ ...prev, pInicial: dateValue }))
      },

      onPeriodoFinalChange(pFinal) {
        if (!pFinal) return

        let dateValue
        if (pFinal instanceof Date) {
          dateValue = pFinal
        } else if (typeof pFinal === 'string') {
          dateValue = new Date(pFinal)
        } else {
          return
        }

        if (isNaN(dateValue.getTime())) return

        setState(prev => ({ ...prev, pFinal: dateValue }))
      },

      onBtnPesquisar(companySelected, tpId, pInicial, pFinal) {
        if (!companySelected) {
          setState(prev => ({ ...prev, errorMessage: 'Selecione uma empresa' }))
          return
        }
        if (!tpId || tpId === 0) {
          setState(prev => ({ ...prev, errorMessage: 'Selecione um TP na barra superior' }))
          return
        }
        if (!(pInicial instanceof Date) || isNaN(pInicial.getTime())) {
          setState(prev => ({ ...prev, errorMessage: 'Período inicial inválido' }))
          return
        }
        if (!(pFinal instanceof Date) || isNaN(pFinal.getTime())) {
          setState(prev => ({ ...prev, errorMessage: 'Período final inválido' }))
          return
        }

        setState(prev => ({ ...prev, loadingMessage: LOAD_MESSAGE.GET_PICKINGS }))
      },

      async getPickingsPorEmpresa(companyId, tpId, pInicial, pFinal) {
        try {
          const data = await ProdutividadeSeparacaoHttp.getPickingsPorEmpresa(
            companyId,
            tpId,
            pInicial,
            pFinal,
          )

          const pickings = Array.isArray(data.pickings) ? data.pickings : []

          // Verifica se não há pickings no período
          if (pickings.length === 0) {
            setState(prev => ({
              ...prev,
              pickings: [],
              pickingOptions: [],
              selectedPickings: [],
              teamOptions: [],
              selectedTeams: [],
              userOptions: [],
              selectedUsers: [],
              report: null,
              loadingMessage: LOAD_MESSAGE.NONE,
              errorMessage: 'Nenhum picking feito no período selecionado',
            }))
            return
          }

          const pickingOptions = pickings.map(p => ({
            label: `${p.id} - ${p.name} (${p.status})`,
            value: p.id,
          }))

          setState(prev => ({
            ...prev,
            pickings,
            pickingOptions,
            selectedPickings: [],
            teamOptions: [],
            selectedTeams: [],
            userOptions: [],
            selectedUsers: [],

            report: null,
            loadingMessage: LOAD_MESSAGE.NONE,
            successMessage: 'Dados carregados. Selecione os pickings.',
          }))
        } catch (e) {
          setState(prev => ({
            ...prev,
            errorMessage: errorHandler(e),
            loadingMessage: LOAD_MESSAGE.NONE,
          }))
        }
      },

      onPickingsChange(selectedPickings) {
        setState(prev => {
          const selectedPickingIds = selectedPickings.map(p => p.value)
          const pickingsSelecionados = prev.pickings.filter(p => selectedPickingIds.includes(p.id))

          const teamsMap = new Map()
          pickingsSelecionados.forEach(p => {
            ;(p.teams || []).forEach(t => {
              if (!teamsMap.has(t.id)) {
                teamsMap.set(t.id, { id: t.id, name: t.name })
              }
            })
          })

          const teamOptions = Array.from(teamsMap.values()).map(t => ({
            label: t.name,
            value: t.id,
          }))

          const usersMap = new Map()
          pickingsSelecionados.forEach(p => {
            ;(p.teams || [])
              .filter(t => teamsMap.has(t.id))
              .forEach(t => {
                ;(t.users || []).forEach(u => {
                  if (!usersMap.has(u.id)) {
                    usersMap.set(u.id, { id: u.id, name: u.name })
                  }
                })
              })
          })

          const userOptions = Array.from(usersMap.values()).map(u => ({
            label: u.name,
            value: u.id,
          }))

          return {
            ...prev,
            selectedPickings,
            teamOptions,
            selectedTeams: teamOptions,
            userOptions,
            selectedUsers: userOptions,
          }
        })
      },

      onTeamsChange(selectedTeams) {
        setState(prev => {
          const selectedTeamIds = selectedTeams.map(t => t.value)
          const selectedPickingIds = prev.selectedPickings.map(p => p.value)
          const pickingsSelecionados = prev.pickings.filter(p => selectedPickingIds.includes(p.id))

          const usersMap = new Map()
          pickingsSelecionados.forEach(p => {
            ;(p.teams || [])
              .filter(t => selectedTeamIds.includes(t.id))
              .forEach(t => {
                ;(t.users || []).forEach(u => {
                  if (!usersMap.has(u.id)) {
                    usersMap.set(u.id, { id: u.id, name: u.name })
                  }
                })
              })
          })

          const userOptions = Array.from(usersMap.values()).map(u => ({
            label: u.name,
            value: u.id,
          }))

          return {
            ...prev,
            selectedTeams,
            userOptions,
            selectedUsers: userOptions,
          }
        })
      },

      onUsersChange(selectedUsers) {
        setState(prev => ({
          ...prev,
          selectedUsers,
        }))
      },

      onGerarRelatorio() {
        setState(prev => {
          if (prev.selectedPickings.length === 0) {
            return { ...prev, errorMessage: 'Selecione ao menos um picking.' }
          }

          const selectedPickingIds = prev.selectedPickings.map(p => p.value)
          const selectedTeamIds = prev.selectedTeams.map(t => t.value)
          const selectedUserIds = prev.selectedUsers.map(u => u.value)

          const pickingsSelecionados = prev.pickings.filter(p => selectedPickingIds.includes(p.id))

          const teamsSelecionadas = []
          pickingsSelecionados.forEach(p => {
            ;(p.teams || [])
              .filter(t => selectedTeamIds.includes(t.id))
              .forEach(t => {
                teamsSelecionadas.push({
                  ...t,
                  _picking: p,
                  _pickingId: p.id,
                  _pickingStatus: p.status,
                })
              })
          })

          if (teamsSelecionadas.length === 0) {
            return { ...prev, errorMessage: 'Selecione ao menos uma equipe.' }
          }

          // ===== Seção Separação =====
          let totalVolumes = 0
          let volumesSeparados = 0

          teamsSelecionadas.forEach(t => {
            totalVolumes += Number(t.qtdVolumes || 0)
            volumesSeparados += Number(t.volSeparados || 0)
          })

          totalVolumes = Number(totalVolumes.toFixed(2))
          volumesSeparados = Number(volumesSeparados.toFixed(2))

          const percentVolSeparado = totalVolumes > 0 ? (volumesSeparados / totalVolumes) * 100 : 0

          const statusCount = new Map()

          pickingsSelecionados.forEach(p => {
            statusCount.set(p.status, (statusCount.get(p.status) || 0) + 1)
          })

          const statusPieData = [
            ...PICKING_STATUS_CONFIG.filter(status => statusCount.has(status.label)).map(
              status => ({
                name: status.label,
                value: statusCount.get(status.label),
                color: status.color,
              }),
            ),
            ...Array.from(statusCount.entries())
              .filter(
                ([statusName]) =>
                  !PICKING_STATUS_CONFIG.some(status => status.label === statusName),
              )
              .map(([statusName, value]) => ({
                name: statusName,
                value,
                color: '#999999',
              })),
          ]

          // ===== Seção Tempos =====
          let tempoTotalSec = 0
          let totalParadoSec = 0
          let paradasAcima10Total = 0

          teamsSelecionadas.forEach(t => {
            tempoTotalSec += timeStrToSeconds(t.duracao)
            totalParadoSec += timeStrToSeconds(t.totalParado)
            paradasAcima10Total += Number(t.paradas10min || 0)
          })

          let maiorParadaSec = 0
          let inicioMaiorParada = '-'
          let fimMaiorParada = '-'

          pickingsSelecionados.forEach(p => {
            const mp = timeStrToSeconds(p.maiorParada)
            if (mp > maiorParadaSec) {
              maiorParadaSec = mp
              inicioMaiorParada = formatDateTimeToBR(p.inicioMaiorParada)
              fimMaiorParada = formatDateTimeToBR(p.fimMaiorParada)
            }
          })

          const tempoLiquidoSec = Math.max(0, tempoTotalSec - totalParadoSec)

          // ===== Seção Produtividade =====
          const usuariosSelecionados = new Set(selectedUserIds)
          const produtividadeMap = new Map()

          teamsSelecionadas.forEach(t => {
            ;(t.users || []).forEach(u => {
              if (!usuariosSelecionados.has(u.id)) return

              const atual = produtividadeMap.get(u.id) || {
                id: u.id,
                name: u.name,
                qtdSeparadas: 0,
                pesoTotal: 0,
                tempoSec: 0,
              }

              atual.qtdSeparadas += Number(u.qtdBipada || 0)
              atual.pesoTotal += Number(u.pesoTotal || 0)
              atual.tempoSec += timeStrToSeconds(u.tempoGasto)

              produtividadeMap.set(u.id, atual)
            })
          })

          const produtividade = Array.from(produtividadeMap.values()).map(u => {
            const tempoMin = u.tempoSec > 0 ? u.tempoSec / 60 : 0
            const bipsMin = tempoMin > 0 ? u.qtdSeparadas / tempoMin : 0
            const qtdSeparadas = u.qtdSeparadas.toFixed(2)
            return {
              ...u,
              qtdSeparadas,
              pesoTotal: u.pesoTotal.toFixed(2),
              tempo: secondsToTimeStr(u.tempoSec),
              bipsMin: Number.isFinite(bipsMin) ? bipsMin.toFixed(2) : '0.00',
            }
          })

          const report = {
            separacao: {
              statusPieData,
              statusPieColors: statusPieData.map(item => item.color),
              totalVolumes: totalVolumes.toFixed(2),
              volumesSeparados: volumesSeparados.toFixed(2),
              percentVolSeparado: percentVolSeparado.toFixed(2),
            },
            tempos: {
              tempoTotal: secondsToTimeStr(tempoTotalSec),
              paradasAcima10: paradasAcima10Total,
              tempoTotalParado: secondsToTimeStr(totalParadoSec),
              maiorParada: secondsToTimeStr(maiorParadaSec),
              inicioMaiorParada,
              fimMaiorParada,
              tempoLiquido: secondsToTimeStr(tempoLiquidoSec),
            },
            produtividade,
          }

          return {
            ...prev,
            report,
            errorMessage: null,
            successMessage: null,
          }
        })
      },
    }
  }, [])

  return { state, localDispatch }
}
